import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSortMode, selectStop, setDriverStops } from '../store/routeSlice';
import { StatusBadge, PriorityBadge, RouteSortBar } from '../components/index';
import { STATUS_STRING_TO_NUMERIC } from '../constants/status';
import { getTodayRoute } from '../services/routeService';
import { FULL_DAYS, MONTHS, REFRESH_INTERVAL_MS, utcTimeToLocal } from '../Utils';
import { showToast } from '../store/uiSlice';
import { loaderActions } from '../store/loaderSlice';
import { MdOutlineRefresh } from "react-icons/md";

// ── Status string constants ────────────────────────────────────────────────
const STATUS_PICKED_UP = 'picked_up';
const STATUS_ARRIVED = 'arrived';
const STATUS_READY = 'ready';
const STATUS_DELIVERED = "delivered"

// Helper: total specimen count from the specimenCounts map { id: count, ... }
function totalSamples(specimenCounts = {}) {
  return Object.values(specimenCounts).reduce((sum, n) => sum + n, 0);
}

// Helper: CSS class for the route-number bubble
function numClass(status) {
  if (status === STATUS_PICKED_UP || status === STATUS_ARRIVED) return 'picked';
  if (status === STATUS_READY) return 'ready';
  return '';
}

function sortStops(stops = [], mode) {
  const arr = [...stops];
  if (mode === 'time') {
    return arr.sort((a, b) =>
      (a.timeWindow ?? '').localeCompare(b.timeWindow ?? '')
    );
  }
  if (mode === 'priority') {
    return arr.sort((a, b) => {
      const aScore = (a.priority ? 2 : 0) + (a.status === STATUS_READY ? 1 : 0);
      const bScore = (b.priority ? 2 : 0) + (b.status === STATUS_READY ? 1 : 0);
      return bScore - aScore;
    });
  }
  return arr;
}

function DriverView({ isActive }) {
  const dispatch = useDispatch();
  const [spinner, setSpinner] = useState(false);
  // const [showAddStop, setShowAddStop] = useState(false);
  const [selectedDay, setSelectedDay] = useState('0');
  const { stops, sortMode } = useSelector((state) => state.route);

  function getDayLabel(offset) {
    const base = new Date();
    base.setDate(base.getDate() + parseInt(offset));
    return `${FULL_DAYS[base.getDay()]}, ${MONTHS[base.getMonth()]} ${base.getDate()}`;
  }

  const route = stops?.[0];
  const allStops = route?.stops ?? [];

  const sorted = sortStops(allStops, sortMode);
  const readyCount = allStops.filter((s) => s.status === STATUS_READY).length;
  const pickedCount = allStops.filter((s) => s.status === STATUS_PICKED_UP || s.status === STATUS_ARRIVED).length;
  const deliveryCount = allStops.filter((s) => s.status === STATUS_DELIVERED).length;
  const totalDriverStops = allStops.length;

  useEffect(() => {
    fetchRoutes();
    if (!isActive) return;
    const interval = setInterval(() => fetchRoutes(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [selectedDay, isActive]);

  const fetchRoutes = async (isPolling = false) => {
    try {
      const base = new Date();
      base.setDate(base.getDate() + parseInt(selectedDay));
      const formattedDate = base.toISOString().split('T')[0];
      if (isPolling) {
        dispatch(loaderActions.fetched());
      }
      const res = await getTodayRoute(formattedDate);
      dispatch(setDriverStops(res?.data));
    } catch (err) {
      console.error('Route fetch failed:', err);
      dispatch(showToast(err?.response?.data?.message));
    }
  };

  const handleRefresh = () => {
    dispatch(showToast("Page Refresh"))
    fetchRoutes(false);
    setSpinner(true);
    setInterval(() => {
      setSpinner(false);
    }, 1800);
  }

  return (
    <div className="view active" style={{ paddingBottom: 80, marginTop: "8vh" }}>
      <div className="date-bar">
        <div className="date-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Viewing — <strong>{getDayLabel(selectedDay)}</strong>
          <span
            title="Auto-refreshing every 20 s"
            style={{
              display: 'inline-block', width: 7, height: 7,
              borderRadius: '50%', background: '#27AE60',
              animation: 'pulse 2s infinite',
              flexShrink: 0,
            }}
          />
        </div>

        <select
          value={selectedDay}
          onChange={(e) => setSelectedDay(e.target.value)}
          style={{
            padding: '4px 10px', border: '1px solid var(--rgray)', borderRadius: 8,
            fontFamily: "'Poppins', sans-serif", fontSize: 12, color: 'var(--rp)',
            fontWeight: 600, outline: 'none', background: 'white', cursor: 'pointer',
          }}
        >
          {[5, 4, 3, 2, 1, 0, -1, -2, -3, -4, -5, -6, -7].map((offset) => {
            const label =
              offset === 0  ? `Today — ${getDayLabel(0)}` :
              offset === -1 ? `Yesterday — ${getDayLabel(-1)}` :
              offset === 1  ? `Tomorrow — ${getDayLabel(1)}` :
              offset > 1    ? `In ${offset} days — ${getDayLabel(offset)}` :
                              getDayLabel(offset);
            return (
              <option key={offset} value={String(offset)}>
                {label}
              </option>
            );
          })}
        </select>

        <MdOutlineRefresh className={spinner ? 'spin-refresh': ''} size={25} onClick={() => handleRefresh()} title='Refresh' style={{ cursor: "pointer" }} />
      </div>

      {/* Summary tiles */}
      <div style={{ padding: '0 16px' }}>
        <div className="driver-summary">
          <div className="driver-stat">
            <div className="driver-stat-num" style={{ color: '#1a7a40' }}>{readyCount}</div>
            <div className="driver-stat-label">Ready now</div>
          </div>
          <div className="driver-stat">
            <div className="driver-stat-num" style={{ color: '#1565C0' }}>{pickedCount}</div>
            <div className="driver-stat-label">Picked up</div>
          </div>
          <div className="driver-stat">
            <div className="driver-stat-num">{deliveryCount}</div>
            <div className="driver-stat-label">Delivered</div>
          </div>
        </div>
      </div>

      {/* Sort control */}
      <RouteSortBar
        sortMode={sortMode}
        onSort={(mode) => dispatch(setSortMode(mode))}
      />

      <div className="section-title" style={{ margin: "10px 0px" }}>My Route Today </div>

      <div style={{ padding: '0 16px' }}>
        {sorted.length > 0 ? sorted.map((s, i) => {
          const nc = numClass(s.status);
          const hasNote = s.notes && s.notes.length > 0;
          const hasPhotos = s.photos && s.photos.length > 0;
          const hasDropFacility = s.dropFacilityName && s.dropFacilityName.length > 0;
          const samples = totalSamples(s.collectedCounts);

          return (
            <div
              key={s.id}
              className={`route-card ${s.priority ? 'priority' : ''}`}
              onClick={() => dispatch(selectStop(s.id))}
            >
              <div className="route-card-top">
                <div className={`route-num ${nc}`}>{i + 1}</div>
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                  <div className="route-info">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div className="route-name">{s.facilityName}</div>
                      {s.priority && <PriorityBadge />}
                    </div>
                    <div className="route-eta">{s.addr}</div>
                  </div>
                  <div className="route-right">
                    <div>
                      <div style={{ marginTop: 4 }}>
                        <StatusBadge status={STATUS_STRING_TO_NUMERIC[s.status] ?? 0} />
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", marginTop: "8x" }}>
                      <div className="route-samples">{samples} samples</div>
                      <div style={{ marginLeft: "5px" }} className="route-time">{utcTimeToLocal(s.timeWindow)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {(hasNote || hasPhotos || hasDropFacility) && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--rgray)', display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                    {hasPhotos && (
                      <div style={{ fontSize: 11, color: 'var(--berry)', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="m21 15-5-5L5 21" />
                        </svg>
                        {s.photos.length} photos
                      </div>
                    )}
                    {hasNote && (
                      <div className="note-preview" style={{ margin: 0, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        ✎ {s.notes}
                      </div>
                    )}
                  </div>
                  {hasDropFacility && (
                    <div
                      title={s.dropFacilityName}
                      style={{
                        fontSize: 11, color: 'var(--bodygray)', display: 'flex', alignItems: 'center',
                        gap: 4, flexShrink: 0, minWidth: 0, maxWidth: '55%',
                      }}
                    >
                      <span style={{ color: '#b36a00', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: "800" }}>
                        📦 Pickup at: {s.dropFacilityName}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }) : <div style={{ fontSize: 13, color: 'var(--bodygray)', textAlign: "center" }}>No Stops Found For today</div>}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

export default DriverView;